import { useCallback } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import { useRecortesExtracaoApi } from "../../../infra/geo-veles";
import { useUIService } from "../../ui";
import {
  removeCreateRecorteFormFiltrosState,
  removeEditRecorteFormFiltrosState,
  removeRecorteState,
  resetCreateRecorteFormState,
  resetEditRecorteFormState,
  resetLayoutRecorteSelecionadoState,
  resetRecorteExtracaoState,
  resetSelectedFilterState,
  setCreateRecorteFormErrorsState,
  setCreateRecorteFormFiltrosState,
  setEditRecorteFormFiltrosState,
  setCreateRecorteFormNomeState,
  setCreateRecorteFormQuantidadeState,
  setCreateRecorteFormTouchedState,
  setDownloadLockedState,
  setOpenFormCreateRecorteState,
  setRecorteErrorsState,
  setRecorteExtracaoState,
  setRecortesDisponiveisState,
  setResetRecorteErrorsState,
  setResetRecortesDisponiveisState,
  setSelectedFilterState,
  validateCreateRecorteExtracaoForm,
  validateEditRecorteExtracaoForm,
  setEditRecorteState,
  setOpenFormEditRecorteState,
  resetRecorteEditandoState,
  setEditRecorteFormNomeState,
  setEditRecorteFormQuantidadeState,
  setEditRecorteFormTouchedState,
  setEditRecorteFormErrorsState,
  setRecorteExtracaoAtualState,
  setUpdateRecorteAtualState,
  setIndexRecorteState,
  resetIndexRecorteState,
} from "../../../application/states/geo-veles";
import {
  IFiltrosProjeto,
  IRecorte,
  IRecorteExtracao,
} from "../../../application/models";

export const useRecortesExtracaoService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const {
    createRecorteExtracaoForm,
    editRecorteExtracaoForm,
    recorteExtracao,
    indexRecorte,
  } = useAppSelector((state) => state.recorteExtracao);
  const { projetoAberto } = useAppSelector((state) => state.projetos);
  // API
  const { findRecorteExtracaoApi, getAnaliseRecorteExtracaoApi } =
    useRecortesExtracaoApi();
  // Hook
  const { setAlert, setLoading } = useUIService();

  // Actions
  const setRecorteExtracao = useCallback(async () => {
    if (projetoAberto) {
      try {
        const findRecortesExtracao = await findRecorteExtracaoApi({
          IdVelesGeoProjeto: String(projetoAberto.IdVelesGeoProjeto),
        });
        if (findRecortesExtracao.IdVelesGeoProjeto) {
          dispatch(setRecorteExtracaoState(findRecortesExtracao));
        } else {
          dispatch(
            setRecorteExtracaoState({
              ...findRecortesExtracao,
              IdVelesGeoProjeto: projetoAberto.IdVelesGeoProjeto,
            })
          );
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error,
          type: "error",
        });
      }
    }
  }, [dispatch, findRecorteExtracaoApi, projetoAberto, setAlert]);

  const resetRecorteExtracao = useCallback(() => {
    dispatch(resetRecorteExtracaoState());
  }, [dispatch]);

  const addRecorteExtracao = useCallback(() => {
    if (projetoAberto) {
      const errors = validateCreateRecorteExtracaoForm(
        createRecorteExtracaoForm.fields
      );

      if (errors.length > 0) {
        dispatch(
          setCreateRecorteFormErrorsState({
            errors: errors,
          })
        );
        dispatch(
          setCreateRecorteFormTouchedState({
            touched: true,
          })
        );
      } else {
        if (
          recorteExtracao.Recortes.find(
            (recorte) => recorte.Nome === createRecorteExtracaoForm.fields.Nome
          )
        ) {

          errors.push({
            key: "Nome",
            message: "Este nome já está sendo usado em outro recorte",
          });
          dispatch(
            setCreateRecorteFormErrorsState({
              errors: errors,
            })
          );
          dispatch(
            setCreateRecorteFormTouchedState({
              touched: true,
            })
          );
        } else {
          dispatch(
            setRecorteExtracaoState({
              ...recorteExtracao,
              IdVelesGeoProjeto: projetoAberto?.IdVelesGeoProjeto,
              Recortes: [
                ...(recorteExtracao.Recortes || []),
                createRecorteExtracaoForm.fields,
              ],
            })
          );

          dispatch(resetCreateRecorteFormState());
          dispatch(resetEditRecorteFormState());
          dispatch(setOpenFormCreateRecorteState(false));
          dispatch(setDownloadLockedState(true));
          dispatch(setResetRecorteErrorsState());
          dispatch(setResetRecortesDisponiveisState());

          setAlert({
            message: `Recorte de ${createRecorteExtracaoForm.fields.Quantidade.toLocaleString(
              "pt-BR"
            )} registros adicionado`,
            type: "success",
          });
        }
      }
    }
  }, [
    createRecorteExtracaoForm,
    dispatch,
    projetoAberto,
    recorteExtracao,
    setAlert,
  ]);

  const obterIndex = useCallback(() => {
    dispatch(resetIndexRecorteState());

    const index = recorteExtracao.Recortes.findIndex(
      (recorte) => recorte.Nome === editRecorteExtracaoForm.fields.Nome
    );
    dispatch(setIndexRecorteState(index));
  }, [dispatch, editRecorteExtracaoForm.fields.Nome, recorteExtracao]);

  const resetIndexRecorte = useCallback(() => {
    dispatch(resetIndexRecorteState());
  }, [dispatch]);

  const updateRecorteExtracao = useCallback(() => {
    if (!projetoAberto) return;

    const editedFields = editRecorteExtracaoForm.fields;
    const errors = validateEditRecorteExtracaoForm(editedFields);

    // 1. Validação de campos
    if (errors.length > 0) {
      dispatch(setEditRecorteFormErrorsState({ errors }));
      dispatch(setEditRecorteFormTouchedState({ touched: true }));
      return;
    }

    // 2. Verifica duplicidade de nome, ignorando o próprio índice sendo editado
    const nomeDuplicado = recorteExtracao.Recortes.some(
      (recorte, index) =>
        recorte.Nome === editedFields.Nome && index !== indexRecorte
    );

    if (nomeDuplicado) {
      dispatch(
        setEditRecorteFormErrorsState({
          errors: [
            ...errors,
            {
              key: "Nome",
              message: "Este nome já está sendo usado em outro recorte",
            },
          ],
        })
      );
      dispatch(setEditRecorteFormTouchedState({ touched: true }));
      return;
    }

    // 3. Atualiza o recorte na lista
    dispatch(
      setUpdateRecorteAtualState({
        index: indexRecorte ?? 0,
        recorte: editedFields,
      })
    );

    // 5. Limpa e fecha formulário
    dispatch(resetEditRecorteFormState());
    dispatch(setOpenFormEditRecorteState(false));
    dispatch(setDownloadLockedState(true));
    dispatch(setResetRecorteErrorsState());
    dispatch(setResetRecortesDisponiveisState());

    setAlert({
      message: `Recorte atualizado com sucesso`,
      type: "success",
    });
  }, [
    editRecorteExtracaoForm,
    dispatch,
    projetoAberto,
    recorteExtracao,
    indexRecorte,
    setAlert,
  ]);

  const resetEditRecorteForm = useCallback(() => {
    dispatch(resetEditRecorteFormState());
  }, [dispatch]);

  const removeRecorte = useCallback(
    (recorte: IRecorte) => {
      if (recorteExtracao.Recortes.length === 0) {
        dispatch(resetLayoutRecorteSelecionadoState());
      }
      dispatch(
        removeRecorteState({
          recorte: recorte,
        })
      );

      dispatch(setDownloadLockedState(true));
      dispatch(setResetRecorteErrorsState());
      dispatch(setResetRecortesDisponiveisState());
    },
     [dispatch]
  );

  const setCreateRecorteFormNome = useCallback(
    (nome: string) => {
      const normalizedName = nome.replace("  ", " ").trim().toUpperCase();
      dispatch(
        setCreateRecorteFormNomeState({
          nome: normalizedName,
        })
      );
    },
    [dispatch]
  );

  const setEditRecorteFormNome = useCallback(
    (nome: string) => {
      const normalizedName = nome.toUpperCase();
      dispatch(
        setEditRecorteFormNomeState({
          nome: normalizedName,
        })
      );
    },
    [dispatch]
  );

  const setCreateRecorteFormFiltros = useCallback(
    (data: { filtro: keyof IFiltrosProjeto; value: any }) => {
      dispatch(
        setCreateRecorteFormFiltrosState({
          key: data.filtro,
          value: data.value,
        })
      );

      setTimeout(() => {}, 500); 
    },
    [dispatch]
  );

  const setEditRecorteFormFiltros = useCallback(
    (data: { filtro: keyof IFiltrosProjeto; value: any }) => {
      dispatch(
        setEditRecorteFormFiltrosState({
          key: data.filtro,
          value: data.value,
        })
      );

      setTimeout(() => {}, 500); 
    },
    [dispatch]
  );

  const removeCreateRecorteFormFiltros = useCallback(
    (data: { filtro: keyof IFiltrosProjeto }) => {
      dispatch(
        removeCreateRecorteFormFiltrosState({
          key: data.filtro,
        })
      );
    },
    [dispatch]
  );
  const removeEditRecorteFormFiltros = useCallback(
    (data: { filtro: keyof IFiltrosProjeto }) => {
      dispatch(
        removeEditRecorteFormFiltrosState({
          key: data.filtro,
        })
      );
    },
    [dispatch]
  );

  const setCreateRecorteFormQuantidade = useCallback(
    (quantidade: number) => {
      dispatch(
        setCreateRecorteFormQuantidadeState({
          quantidade: quantidade,
        })
      );
    },
    [dispatch]
  );

  const setEditRecorteFormQuantidade = useCallback(
    (quantidade: number) => {
      dispatch(
        setEditRecorteFormQuantidadeState({
          quantidade: quantidade,
        })
      );
    },
    [dispatch]
  );

  const resetCreateRecorteForm = useCallback(() => {
    dispatch(resetCreateRecorteFormState());
  }, [dispatch]);

  const openCreateRecorteForm = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenFormCreateRecorteState(openForm));
    },
    [dispatch]
  );

  const setSelectedFilter = useCallback(
    (filter: keyof IFiltrosProjeto) => {
      dispatch(setSelectedFilterState(filter));
    },
    [dispatch]
  );

  const resetSelectedFilter = useCallback(() => {
    dispatch(resetSelectedFilterState());
  }, [dispatch]);

  const setDownloadLocked = useCallback(
    (downloadLocked: boolean) => {
      dispatch(setDownloadLockedState(downloadLocked));
    },
    [dispatch]
  );

  const analiseRecorteExtracao = useCallback(async () => {
    if (projetoAberto) {
      const loadingId = Math.random().toString();
      try {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Analisando recortes...",
        });

        const getAnaliseRecorteExtracao = await getAnaliseRecorteExtracaoApi({
          recorteExtracao: recorteExtracao,
        });

        dispatch(
          setRecorteErrorsState(getAnaliseRecorteExtracao.recorteErrors)
        );
        dispatch(
          setRecortesDisponiveisState(
            getAnaliseRecorteExtracao.recortesDisponiveis
          )
        );

        if (getAnaliseRecorteExtracao.recorteErrors.length === 0) {
          dispatch(setDownloadLockedState(false));
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error,
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    }
  }, [
    dispatch,
    getAnaliseRecorteExtracaoApi,
    projetoAberto,
    recorteExtracao,
    setAlert,
    setLoading,
  ]);

  const openEditRecorteForm = useCallback(
    (data: { openForm: boolean; recorte: IRecorte; index: number }) => {
      dispatch(setOpenFormEditRecorteState(false));
      setTimeout(() => {
        dispatch(setEditRecorteState(data.recorte));
        dispatch(setIndexRecorteState(data.index));
        dispatch(setOpenFormEditRecorteState(data.openForm));
      }, 0);
    },
    [dispatch]
  );

  const closeEditRecorteForm = (open: boolean) => {
    dispatch(setOpenFormEditRecorteState(open));
  };

  const resetRecorteEditando = () => {
    dispatch(resetRecorteEditandoState());
  };

  const setEditRecorteForm = (recorte: IRecorte) => {
    dispatch(setEditRecorteState(recorte));
  };

  const setRecortesAtuais = (recortes: IRecorteExtracao) => {
    dispatch(
      setRecorteExtracaoAtualState({
        ...recortes,
      })
    );
  };

  const syncFilterWithForm = useCallback(
    <T extends keyof IFiltrosProjeto>(data: {
      filtro: T;
      value: IFiltrosProjeto[T];
      formType: 'create' | 'edit';
    }) => {
      if (data.formType === 'create') {
        dispatch(
          setCreateRecorteFormFiltrosState({
            key: data.filtro,
            value: data.value,
          })
        );
      } else {
        dispatch(
          setEditRecorteFormFiltrosState({
            key: data.filtro,
            value: data.value,
          })
        );
      }

      setTimeout(() => { }, 500);
    },
    [dispatch]
  );

  const removeFilterFromForm = useCallback(
    <T extends keyof IFiltrosProjeto>(data: {
      filtro: T;
      formType: 'create' | 'edit';
    }) => {
      if (data.formType === 'create') {
        dispatch(
          removeCreateRecorteFormFiltrosState({
            key: data.filtro,
          })
        );
      } else {
        dispatch(
          removeEditRecorteFormFiltrosState({
            key: data.filtro,
          })
        );
      }
    },
    [dispatch]
  );


  return {
    // CREATE
    setCreateRecorteFormNome,
    setCreateRecorteFormFiltros,
    removeCreateRecorteFormFiltros,
    setCreateRecorteFormQuantidade,
    resetCreateRecorteForm,
    addRecorteExtracao,
    openCreateRecorteForm,

    // EDIT
    setEditRecorteForm,
    resetEditRecorteForm,
    setEditRecorteFormNome,
    setEditRecorteFormFiltros,
    removeEditRecorteFormFiltros,
    setEditRecorteFormQuantidade,
    resetRecorteEditando,
    updateRecorteExtracao,
    openEditRecorteForm,
    closeEditRecorteForm,

    // VIEW / SELEÇÃO
    setRecorteExtracao,
    setRecortesAtuais,
    obterIndex,
    resetIndexRecorte,

    // REMOVE
    removeRecorte,

    // FILTROS
    setSelectedFilter,
    resetSelectedFilter,

    // OUTROS
    resetRecorteExtracao,
    setDownloadLocked,
    analiseRecorteExtracao,
    syncFilterWithForm,
    removeFilterFromForm,
  };
};
